import { kApdmCampaignStatusLabels } from '@euler/functions/apdm/campaignLabels';
import {
  ApdmBuildStep,
  ApdmCampaignControl,
  ApdmCampaignOutcome,
  ApdmCampaignStatus,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import {
  CirclePause,
  CirclePlay,
  CircleStop,
  LucideIcon,
} from 'lucide-react-native';
import { memo, useContext, useState } from 'react';
import {
  AiChatCampaignContext,
  useAiChatActions,
  useAiChatMessage,
} from '../chat/AiChatActions';
import { CardNotice, ChatCard } from '../components/ChatCard';
import { PrimaryButton } from '../components/PrimaryButton';
import { aiColors } from '../theme';

interface ControlCopy {
  title: string;
  description: string;
  button: string;
  done: string;
  icon: LucideIcon;
  color: string;
  outcome: ApdmCampaignOutcome;
  /** The states the action applies to. */
  from: ApdmCampaignStatus[];
}

const kControls: Record<ApdmCampaignControl, ControlCopy> = {
  [ApdmCampaignControl.Pause]: {
    title: '暂停活动',
    description: '暂停后不再发送新消息，已发出的不受影响，随时可以继续。',
    button: '暂停活动',
    done: '活动已暂停。',
    icon: CirclePause,
    color: aiColors.amber,
    outcome: ApdmCampaignOutcome.Paused,
    from: [ApdmCampaignStatus.Reaching],
  },
  [ApdmCampaignControl.Resume]: {
    title: '继续活动',
    description: '继续后会在下一个每日时段接着发送。',
    button: '继续活动',
    done: '活动已继续。',
    icon: CirclePlay,
    color: aiColors.green,
    outcome: ApdmCampaignOutcome.Resumed,
    from: [ApdmCampaignStatus.Paused],
  },
  [ApdmCampaignControl.Stop]: {
    title: '停止活动',
    description:
      '停止后活动结束，不再发送，也不能再继续。已发出的消息和线索都会保留。',
    button: '确认停止',
    done: '活动已停止。',
    icon: CircleStop,
    color: aiColors.destructive,
    outcome: ApdmCampaignOutcome.Stopped,
    from: [
      ApdmCampaignStatus.Scheduled,
      ApdmCampaignStatus.Reaching,
      ApdmCampaignStatus.Paused,
      ApdmCampaignStatus.Reached,
    ],
  },
};

function parseControl(data: unknown): ApdmCampaignControl | null {
  const action =
    data && typeof data === 'object' && 'action' in data
      ? (data as { action: unknown }).action
      : null;
  return Object.values(ApdmCampaignControl).includes(
    action as ApdmCampaignControl,
  )
    ? (action as ApdmCampaignControl)
    : null;
}

/**
 * Pause, resume or stop the campaign this conversation launched. The AI
 * only offers it; the user's tap does it, and the conversation hears so.
 */
export const CampaignControlCard = memo(
  ({ data, streaming }: { data: unknown; streaming?: boolean }) => {
    const control = parseControl(data);
    const campaign = useContext(AiChatCampaignContext);
    const chat = useAiChatActions();
    const { latest } = useAiChatMessage();
    const { apdmCampaignService } = useServiceFactory();
    const [working, setWorking] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    if (streaming || !control) return null;
    const copy = kControls[control];
    const applies = !!campaign && copy.from.includes(campaign.status);
    const act = async () => {
      if (!campaign || working) return;
      setWorking(true);
      setFailure(null);
      try {
        const { summary } = await apdmCampaignService.detail({
          campaignId: campaign.campaignId,
        });
        const ref = {
          campaignId: campaign.campaignId,
          expectedVersion: summary.version,
        };
        if (control === ApdmCampaignControl.Pause)
          await apdmCampaignService.pause(ref);
        else if (control === ApdmCampaignControl.Resume)
          await apdmCampaignService.resume(ref);
        else await apdmCampaignService.end(ref);
        setDone(true);
        chat.send(copy.done, {
          answer: {
            step: ApdmBuildStep.Launch,
            campaignId: campaign.campaignId,
            outcome: copy.outcome,
          },
        });
      } catch (error) {
        console.error('APDM campaign control failed', error);
        setFailure('没有操作成功，请稍后再试。');
      } finally {
        setWorking(false);
      }
    };
    return (
      <ChatCard
        icon={copy.icon}
        color={copy.color}
        title={copy.title}
        subtitle={campaign?.title}
      >
        <CardNotice
          text={
            done
              ? copy.done
              : applies || !campaign
              ? copy.description
              : `活动现在是「${
                  kApdmCampaignStatusLabels[campaign.status]
                }」，不能${copy.title}。`
          }
        />
        {failure ? <CardNotice tone="danger" text={failure} /> : null}
        {!done && latest && applies ? (
          <PrimaryButton
            title={copy.button}
            loading={working}
            disabled={chat.busy}
            onPress={() => void act()}
          />
        ) : null}
      </ChatCard>
    );
  },
);
